import React, { FormEvent, useEffect, useState } from 'react'
import { Country } from 'country-state-city'
import { CountryCode, isValidPhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js'
import { useLocation, useNavigate } from 'react-router-dom'
import Footer from '../component/Footer'
import Header from '../component/Header'
import { signup } from '../services/authService'
import { getPlans } from '../services/planService'
import {
  createPaymentSession,
  submitPayment,
  validateIban,
  verifyPayment,
} from '../services/subscriptionService'
import { useAuth } from '../context/AuthContext'

type CheckoutState = {
  plan_id?: number
  plan_key?: string
  plan_name?: string
  price?: number
}

type FieldErrors = {
  email?: string
  phone?: string
}

const IPINFO_LITE_TOKEN = '091f7fb53966b8'
const FALLBACK_COUNTRY_CODE = 'CY'

const normalizePlanName = (name: string) =>
  name.toLowerCase().replace(/\s+monthly$/, '').replace(/^pro$/, 'premium')

const billingCountries = Country.getAllCountries()
  .map((country) => ({
    name: country.name,
    isoCode: country.isoCode,
  }))
  .sort((a, b) => a.name.localeCompare(b.name))

const countryCodes = billingCountries.reduce<Record<string, string>>(
  (codes, country) => ({
    ...codes,
    [country.name]: country.isoCode,
  }),
  {},
)

const phoneCountries = Country.getAllCountries()
  .map((country) => ({
    name: country.name,
    isoCode: country.isoCode,
    dialCode: country.phonecode.startsWith('+')
      ? country.phonecode
      : `+${country.phonecode}`,
    normalizedDialCode: `+${country.phonecode.replace(/\D/g, '')}`,
  }))
  .filter((country) => country.normalizedDialCode.length > 1)
  .sort((a, b) => a.name.localeCompare(b.name))

const getBillingCountryByIso = (countryCode: string) =>
  billingCountries.find((country) => country.isoCode === countryCode.toUpperCase())

const createInitialForm = () => {
  const fallbackCountry = getBillingCountryByIso(FALLBACK_COUNTRY_CODE)

  return {
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
    phoneCountry: fallbackCountry?.isoCode || FALLBACK_COUNTRY_CODE,
    address: '',
    city: '',
    postalCode: '',
    country: fallbackCountry?.name || 'Cyprus',
    iban: '',
  }
}

type CheckoutForm = ReturnType<typeof createInitialForm>

const splitFullName = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean)

  return {
    firstName: parts[0] || '',
    lastName: parts.slice(1).join(' '),
  }
}

const normalizePhone = (phone: string) =>
  phone.trim().replace(/[\s()-]/g, '')

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
const allowedPhonePattern = /^[+\d\s()-]+$/

const getPhoneCountry = (countryName: string) => {
  const countryCode = countryName?.toUpperCase()

  return phoneCountries.find((country) => country.isoCode === countryCode) ||
  phoneCountries.find((country) => country.isoCode === countryCodes[countryName]) ||
  phoneCountries.find((country) => country.name === countryName) ||
  phoneCountries.find((country) => country.isoCode === FALLBACK_COUNTRY_CODE) ||
  phoneCountries[0]
}

const getNormalizedPhone = (phoneCountry: string, phone: string) => {
  const selectedCountry = getPhoneCountry(phoneCountry)
  const parsedPhone = parsePhoneNumberFromString(
    phone.trim(),
    selectedCountry?.isoCode as CountryCode | undefined,
  )

  if (parsedPhone?.isValid()) {
    return parsedPhone.number
  }

  const phoneValue = normalizePhone(phone)
  const dialCode = selectedCountry?.normalizedDialCode || '+357'

  return `${dialCode}${phoneValue.replace(/^0+/, '')}`
}

const validateEmailValue = (email: string) => {
  if (!email.trim()) {
    return 'Email is required.'
  }

  if (!emailPattern.test(email.trim())) {
    return 'Enter a valid email address.'
  }

  return ''
}

const validatePhoneValue = (phoneCountry: string, phone: string) => {
  if (!phone.trim()) {
    return 'Phone number is required.'
  }

  const selectedCountry = getPhoneCountry(phoneCountry)

  if (!allowedPhonePattern.test(phone.trim())) {
    return 'Phone number can only include digits, spaces, dashes, parentheses, and +.'
  }

  const selectedCountryCode = selectedCountry?.isoCode as CountryCode | undefined
  const phoneValue = phone.trim()
  const parsedPhone = parsePhoneNumberFromString(phoneValue, selectedCountryCode)

  if (!isValidPhoneNumber(phoneValue, selectedCountryCode)) {
    return `Enter a valid ${selectedCountry?.name || 'selected country'} phone number.`
  }

  if (phoneValue.startsWith('+') && parsedPhone?.country && parsedPhone.country !== selectedCountry?.isoCode) {
    return `Enter a valid ${selectedCountry?.name || 'selected country'} phone number.`
  }

  return ''
}

const Checkout = () => {
  const { state } = useLocation()
  const navigate = useNavigate()
  const { isLoggedIn, currentUser, loginUser, setSubscriptionStatus } = useAuth()
  const checkoutState = (state || {}) as CheckoutState
  const planId = checkoutState.plan_id
  const planKey = checkoutState.plan_key
  const planName = checkoutState.plan_name || 'Standard Monthly'
  const planTitle = planName.replace(' Monthly', '')
  const price = checkoutState.price || 59
  const [form, setForm] = useState<CheckoutForm>(createInitialForm)
  const [error, setError] = useState('')
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isDetectingCountry, setIsDetectingCountry] = useState(true)
  const [showPassword, setShowPassword] = useState(false)
  const [showPaymentPopup, setShowPaymentPopup] = useState(false)
  const [redirectCountdown, setRedirectCountdown] = useState(10)

  const updateField = (field: keyof CheckoutForm, value: string) => {
    setForm((current) => ({ ...current, [field]: value }))

    if (field === 'email') {
      setFieldErrors((current) => ({
        ...current,
        email: current.email ? validateEmailValue(value) : current.email,
      }))
    }

    if (field === 'phone') {
      setFieldErrors((current) => ({
        ...current,
        phone: current.phone ? validatePhoneValue(form.phoneCountry, value) : current.phone,
      }))
    }
  }

  const updateCountry = (country: string) => {
    const nextPhoneCountry = countryCodes[country]
    setForm((current) => ({
      ...current,
      country,
      phoneCountry: nextPhoneCountry || current.phoneCountry,
    }))

    if (nextPhoneCountry) {
      setFieldErrors((current) => ({
        ...current,
        phone: current.phone ? validatePhoneValue(nextPhoneCountry, form.phone) : current.phone,
      }))
    }
  }

  const updatePhoneCountry = (phoneCountry: string) => {
    setForm((current) => ({ ...current, phoneCountry }))
    setFieldErrors((current) => ({
      ...current,
      phone: current.phone ? validatePhoneValue(phoneCountry, form.phone) : current.phone,
    }))
  }

  useEffect(() => {
    let isMounted = true

    const detectCountry = async () => {
      let detectedCountry = getBillingCountryByIso(FALLBACK_COUNTRY_CODE)

      try {
        const response = await fetch(`https://api.ipinfo.io/lite/me?token=${IPINFO_LITE_TOKEN}`)
        if (!response.ok) {
          throw new Error('Country detection failed.')
        }

        const data = await response.json()
        detectedCountry = getBillingCountryByIso(String(data?.country_code || '')) || detectedCountry
      } catch {
        detectedCountry = getBillingCountryByIso(FALLBACK_COUNTRY_CODE)
      }

      if (!isMounted || !detectedCountry) return

      const country = detectedCountry

      setForm((current) => ({
        ...current,
        country: country.name,
        phoneCountry: country.isoCode,
      }))
      setIsDetectingCountry(false)
    }

    detectCountry()

    return () => {
      isMounted = false
    }
  }, [])

  useEffect(() => {
    if (!isLoggedIn || !currentUser) return

    const { firstName, lastName } = splitFullName(currentUser.name)
    const savedFirstName = currentUser.user_meta?.first_name || firstName
    const savedLastName = currentUser.user_meta?.last_name || lastName

    setForm((current) => ({
      ...current,
      email: current.email || currentUser.email || '',
      firstName: current.firstName || savedFirstName,
      lastName: current.lastName || savedLastName,
    }))
  }, [currentUser, isLoggedIn])

  useEffect(() => {
    if (!showPaymentPopup) return

    setRedirectCountdown(10)

    const intervalId = window.setInterval(() => {
      setRedirectCountdown((current) => Math.max(current - 1, 0))
    }, 1000)
    const timeoutId = window.setTimeout(() => {
      navigate('/account/')
    }, 10000)

    return () => {
      window.clearInterval(intervalId)
      window.clearTimeout(timeoutId)
    }
  }, [navigate, showPaymentPopup])

  const validateForm = () => {
    const emailError = validateEmailValue(form.email)
    const phoneError = validatePhoneValue(form.phoneCountry, form.phone)

    if (emailError) {
      return emailError
    }

    if (!isLoggedIn && form.password.length < 6) {
      return 'Password must be at least 6 characters.'
    }

    if (!form.firstName.trim() || !form.lastName.trim()) {
      return 'Enter your first and last name.'
    }

    if (!planId && !planKey) {
      return 'Select a valid subscription plan before checkout.'
    }

    if (phoneError) {
      return phoneError
    }

    if (!form.address.trim() || !form.city.trim() || !form.postalCode.trim()) {
      return 'Enter your billing address.'
    }

    if (form.iban.replace(/\s+/g, '').length < 15) {
      return 'Enter a valid IBAN.'
    }

    return ''
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const nextFieldErrors = {
      email: validateEmailValue(form.email),
      phone: validatePhoneValue(form.phoneCountry, form.phone),
    }
    setFieldErrors(nextFieldErrors)

    const validationMessage = validateForm()
    if (validationMessage) {
      setError(validationMessage)
      return
    }

    setError('')
    setIsSubmitting(true)

    try {
      let token = localStorage.getItem('token') || ''
      const normalizedEmail = form.email.trim()
      const normalizedPhone = getNormalizedPhone(form.phoneCountry, form.phone)
      let selectedPlanId = planId

      if (!selectedPlanId && planKey) {
        const plansResponse = await getPlans()
        const matchingPlan = (plansResponse.data || []).find((plan) =>
          plan.is_active && normalizePlanName(plan.name) === planKey,
        )

        if (!matchingPlan?.id) {
          throw new Error('This subscription plan is unavailable right now.')
        }

        selectedPlanId = matchingPlan.id
      }

      if (!selectedPlanId) {
        throw new Error('Select a valid subscription plan before checkout.')
      }

      if (!isLoggedIn) {
        const signupResponse = await signup({
          name: `${form.firstName.trim()} ${form.lastName.trim()}`,
          email: normalizedEmail,
          password: form.password,
          plan_id: selectedPlanId,
          user_meta: {
            phone: normalizedPhone,
            address: form.address.trim(),
            city: form.city.trim(),
            postal_code: form.postalCode.trim(),
            country: countryCodes[form.country],
          },
        })

        token = signupResponse.data.token
        loginUser(token, signupResponse.data.user)
      }

      if (!token) {
        throw new Error('Please log in to complete payment.')
      }

      const sessionResponse = await createPaymentSession(token, {
        plan_id: selectedPlanId,
        plan_name: planName,
        amount: price,
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        email: normalizedEmail,
        country: countryCodes[form.country],
        address: form.address.trim(),
        city: form.city.trim(),
        postal_code: form.postalCode.trim(),
        phone: normalizedPhone,
      })

      const sessionToken = sessionResponse.data.sessionToken
      const ibanResponse = await validateIban(token, {
        session_token: sessionToken,
        iban: form.iban,
      })

      if (!ibanResponse.data.valid) {
        throw new Error(ibanResponse.data.message || 'IBAN validation failed.')
      }

      await submitPayment(token, {
        session_token: sessionToken,
        iban: form.iban,
        iban_holder_name: `${form.firstName.trim()} ${form.lastName.trim()}`,
      })

      const verifyResponse = await verifyPayment(token, {
        session_token: sessionToken,
      })

      if (verifyResponse.success) {
        setSubscriptionStatus(verifyResponse.data.subscriptionActive || verifyResponse.data.status === 'COMPLETED')
        setRedirectCountdown(10)
        setShowPaymentPopup(true)
        return
      }

      throw new Error(verifyResponse.message || 'Payment verification failed.')
    } catch (err: any) {
      setError(err.message || 'Payment could not be completed.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const selectedPhoneCountry = getPhoneCountry(form.phoneCountry)
  const selectedPhoneDialCode = selectedPhoneCountry?.dialCode || '+357'
  const selectedPhoneDisplayDialCode = selectedPhoneCountry?.normalizedDialCode || '+357'
  const selectedPhoneFlag = selectedPhoneCountry
    ? `https://flagcdn.com/w40/${selectedPhoneCountry.isoCode.toLowerCase()}.png`
    : ''

  return (
    <div>
      <Header />

      <main className="main">
        <div className="container px-6 checkout-page-wrap lg:px-12">
          <div className="checkout-panel-card">
            <form className="details-card" onSubmit={handleSubmit} noValidate>
              <h2 className="font-bold checkout-title">
                Complete Payment for {planTitle}
              </h2>
              <p className="checkout-note">
                Server-to-server checkout: validate your IBAN and payment will
                be submitted automatically.
              </p>
              <h2 className="font-bold checkout-title">Your Information</h2>
              <div className="billing-details-fields">
                <div className={`form-row ${isLoggedIn ? 'single-field-row' : ''}`}>
                  <div className="form-group full">
                    <label>
                      Email <span>*</span>
                    </label>
                    <input
                      type="email"
                      placeholder="john.doe@example.com "
                      value={form.email}
                      onChange={(event) => updateField('email', event.target.value)}
                      onBlur={() =>
                        setFieldErrors((current) => ({
                          ...current,
                          email: validateEmailValue(form.email),
                        }))
                      }
                      aria-invalid={Boolean(fieldErrors.email)}
                      required
                    />
                    {fieldErrors.email && (
                      <p className="checkout-field-error">{fieldErrors.email}</p>
                    )}
                  </div>
                  {!isLoggedIn && (
                    <div className="form-group full">
                      <label>
                        Password <span>*</span>
                      </label>
                      <span className="password-input">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Password"
                          name="account_password"
                          id="account_password"
                          value={form.password}
                          onChange={(event) => updateField('password', event.target.value)}
                          required
                        />
                        <button
                          type="button"
                          className="show-password-input"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          aria-describedby="account_password"
                          onClick={() => setShowPassword((current) => !current)}
                        />
                      </span>
                    </div>
                  )}
                </div>
                <div className="form-row">
                  <div className="form-group full">
                    <label>
                      First name <span>*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="John"
                      value={form.firstName}
                      onChange={(event) => updateField('firstName', event.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group full">
                    <label>
                      Last name <span>*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Doe"
                      value={form.lastName}
                      onChange={(event) => updateField('lastName', event.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="form-group full">
                  <label>
                    Phone <span>*</span>
                  </label>
                  <div className="phone-input-row">
                    <div className="phone-code-select select-wrap">
                      {selectedPhoneFlag && (
                        <img src={selectedPhoneFlag} alt="" className="phone-flag" />
                      )}
                      <span className="phone-code-display">{selectedPhoneDisplayDialCode}</span>
                      <select
                        className="custom-select h-[46px]"
                        aria-label="Phone country code"
                        value={form.phoneCountry}
                        onChange={(event) => updatePhoneCountry(event.target.value)}
                        disabled={isDetectingCountry}
                        required
                      >
                        {phoneCountries.map((country) => (
                          <option key={country.isoCode} value={country.isoCode}>
                            {country.dialCode} {country.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <input
                      type="tel"
                      placeholder={`${selectedPhoneDialCode} 70 123 45 67`}
                      value={form.phone}
                      onChange={(event) => updateField('phone', event.target.value)}
                      onBlur={() =>
                        setFieldErrors((current) => ({
                          ...current,
                          phone: validatePhoneValue(form.phoneCountry, form.phone),
                        }))
                      }
                      aria-invalid={Boolean(fieldErrors.phone)}
                      disabled={isDetectingCountry}
                      inputMode="tel"
                      required
                    />
                  </div>
                  {fieldErrors.phone && (
                    <p className="checkout-field-error">{fieldErrors.phone}</p>
                  )}
                </div>
              </div>
              <h2 className="font-bold checkout-title">Your Billing Details</h2>
              <div className="billing-details-fields">
                <div className="form-row">
                  <div className="form-group full">
                    <label>
                      Street address <span>*</span>
                    </label>
                    <input
                      type="text"
                      value={form.address}
                      onChange={(event) => updateField('address', event.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group full">
                    <label>
                      Town / City <span>*</span>
                    </label>
                    <input
                      type="text"
                      value={form.city}
                      onChange={(event) => updateField('city', event.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="form-row bottom-row">
                  <div className="form-group postcode">
                    <label>
                      Postcode / ZIP <span>*</span>
                    </label>
                    <input
                      type="text"
                      value={form.postalCode}
                      onChange={(event) => updateField('postalCode', event.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group country">
                    <label>
                      Country <span>*</span>
                    </label>
                    <div className="select-wrap">
                      <select
                        className="custom-select h-[46px]"
                        value={form.country}
                        onChange={(event) => updateCountry(event.target.value)}
                        disabled={isDetectingCountry}
                        required
                      >
                        {billingCountries.map((country) => (
                          <option key={country.isoCode} value={country.name}>
                            {country.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              <h2 className="font-bold checkout-title">
                SEPA Direct Debit Information
              </h2>
              <div className="payment-box">
                <label className="payment-header">
                  <input type="radio" name="payment" id="sepaRadio" checked readOnly />
                  <div className="custom-radio" />
                  <span className="payment-title">SEPA Direct Debit</span>
                  <img src="/sepa.svg" alt="SEPA" />
                </label>
                <div className="payment-content">
                  <p className="checkout-note">
                    Mandate Information.
                    <br /> By providing your IBAN and confirming this payment,
                    you are authorizing us to send instructions to your bank to
                    debit your account and your bank to debit your account in
                    accordance with those instructions. You are entitled to a
                    refund from your bank under the terms and conditions of your
                    agreement with your bank. A refund must be claimed within 8
                    weeks starting from the date on which your account was
                    debited.
                  </p>
                  <div className="checkout-fields">
                    <label>
                      IBAN <span>*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="IBAN (e.g. DE89370400440532013000)"
                      autoComplete="off"
                      className="checkout-input"
                      value={form.iban}
                      onChange={(event) => updateField('iban', event.target.value)}
                      required
                    />
                  </div>
                  <h2 className="font-bold checkout-title">
                    Payment Information
                  </h2>
                  <p className="mb-0 checkout-note">
                    All transactions are secure and encrypted. Credit card
                    information is never stored on our servers.
                  </p>
                </div>
              </div>
              <p className="mt-3 mb-1 text-center text-[12px] font-medium leading-snug text-[#4b5563]">
                Your personal data will be used to process your order, support
                your experience throughout this website, and for other purposes
                described in our privacy policy .
              </p>
              <div className="place-btn-wrapper">
                {error && (
                  <p className="mb-3 text-center text-sm font-semibold text-red-600">
                    {error}
                  </p>
                )}
                <label className="mb-4 flex items-start gap-3 text-left text-[15px] font-medium leading-snug text-[#4b5563]">
                  <input
                    type="checkbox"
                    name="payment_acknowledgement"
                    className="mt-1 h-4 w-4 shrink-0 accent-[#f59e0b]"
                  />
                  <span>
                    By proceeding with this payment, I confirm that I have read and agree to the{' '}
                    <a href="/terms-and-conditions" className="text-[#f59e0b] underline hover:text-zinc-950">
                      Terms and Conditions
                    </a>{' '}
                    and acknowledge the{' '}
                    <a href="/privacy-policy" className="text-[#f59e0b] underline hover:text-zinc-950">
                      Privacy Policy
                    </a>.
                  </span>
                </label>
                <button
                  type="submit"
                  disabled
                  className="font-bold place-order w-[100%] cursor-not-allowed rounded-[999px] bg-[#f59e0b] px-5 py-4 text-center font-black uppercase tracking-widest text-zinc-950 opacity-60 shadow-[0_0_20px_rgba(163,230,53,0.3)]"
                  name="place_order"
                  id="place_order"
                  value="Complete Your Order Now"
                  data-value="Complete Your Order Now"
                >
                  {isSubmitting ? <span className="checkout-spinner" aria-label="Processing payment" /> : 'Complete Your Order Now'}
                </button>
              </div>
              <div className="sepa-icon">
                <img
                  src="/SEPA Logo PNG Vector (SVG) Free Download.jpg"
                  alt="SEPA"
                />
              </div>
              <div className="checkout-features">
                <div className="feature-item">
                  <div className="flex items-center justify-center h-12 transition-all border min-w-12 feature-title bg-zinc-900 border-zinc-700 rounded-2xl">
                    <img src="/time.svg" alt="" />
                  </div>
                  <div className="feature-text text-[#4b5563]">
                    <strong>Cancel Anytime</strong>
                    Cancel anytime, see T&amp;Cs for more info
                  </div>
                </div>
                <div className="feature-item">
                  <div className="flex items-center justify-center h-12 transition-all border min-w-12 feature-title bg-zinc-900 border-zinc-700 rounded-2xl">
                    <img src="/lock.svg" alt="" />
                  </div>
                  <div className="feature-text text-[#4b5563]">
                    <strong>Secure Processing</strong>
                    Encrypted, secure order processing
                  </div>
                </div>
              </div>
             
              
            </form>
            <div className="subscription-card shadow-[0_20px_50px_rgba(245,158,11,0.2)]">
              <h2>Subscription Information</h2>
              <div className="subscription-item">
                <div className="product-image">
                  <img src="/favicon.ico" alt="product" />
                </div>
                <div className="product-info">
                  <div className="top-row">
                    <h3>{planName} Subscription -</h3>
                    <span className="price">&euro;{price}</span>
                  </div>
                  <p>Rebills every month</p>
                  <div className="font-medium billing-info text-[#f59e0b] text-[14px]">
                    <span>Next Billing:</span>
                    <span>2026-06-11</span>
                  </div>
                </div>
              </div>

              <div className="divider" />
              <div className="summary-row">
                <span>Subtotal</span>
                <span>&euro;{price}</span>
              </div>
              <div className="divider" />
              <div className="total-row">
                <span>Total</span>
                <span>&euro;{price}</span>
              </div>

              <div className="guarantee-box">
                <div className="logo-wrap">
                  <img src="/direct debit.png" alt="Direct Debit Logo" />
                </div>
                <div className="guarantee-title">Direct Debit Guarantee</div>
              </div>

            </div>
          </div>
           <p className="mt-8 font-semibold text-center text-[#52525b] text-[16px]">
                We use GoCardless to process your Direct Debit payments. More
                information on how GoCardless processes your personal data and
                your data protection rights, including your right to object, is
                available at{" "}
                <a href="https://gocardless.com/legal/privacy/" target="_blank" rel="noopener noreferrer">
                  gocardless.com/legal/privacy/
                </a>
              </p>
        </div>
      </main>

      {showPaymentPopup && (
        <div className="checkout-popup-overlay" role="dialog" aria-modal="true" aria-labelledby="payment-success-title">
          <div className="checkout-popup">
            <h2 id="payment-success-title">Payment Submitted</h2>
            <p>Your payment request has been received. The amount will be deducted from your IBAN within 2-3 business days.</p>
            <p className="checkout-popup-countdown">
              Redirecting you to your profile in {redirectCountdown} seconds.
            </p>
            <button type="button" onClick={() => navigate('/account/')}>
              OK
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  )
}

export default Checkout
